import type { ReactNode } from "react";

import clsx from "clsx";

import type {
  GetLanguageLabel,
  MarketingBannerRow,
  MarketingBannersTranslations,
} from "./types";

import { isNoCampaignName } from "../Campaign/isNoCampaignName";
import { formatRebateProgram } from "../ReferralLinkModals";
import { Typo } from "../Typo";
import { dayjs } from "../utils/date";

type BannerInfoListProps = {
  banner: MarketingBannerRow;
  translations: MarketingBannersTranslations;
  getLanguageLabel: GetLanguageLabel;
  showLastClicked?: boolean;
  showCampaign?: boolean;
};

export const BannerInfoList = ({
  banner,
  translations,
  getLanguageLabel,
  showLastClicked = false,
  showCampaign = false,
}: BannerInfoListProps) => {
  const { labels, types, sectionTitle } = translations.preview;
  const createdDate = dayjs(banner.created_at);
  const createdLabel = createdDate.isValid()
    ? createdDate.format("DD MMM YYYY, HH:mm:ss")
    : "";
  const languageLabel = getLanguageLabel(banner.language.trim() || "en");

  const lastClickedDate = banner.last_clicked_at
    ? dayjs(banner.last_clicked_at)
    : undefined;
  const hasLastClick = Boolean(lastClickedDate && lastClickedDate.isValid());
  const lastClickedLabel = hasLastClick
    ? lastClickedDate!.format("DD MMM YYYY")
    : translations.lastClickCell.noClicksYet;

  const campaignName = isNoCampaignName(banner.campaign?.name)
    ? undefined
    : banner.campaign?.name;

  return (
    <div className="flex w-full flex-col gap-4">
      <Typo type="headingSM" textColor="text-text-primary">
        {sectionTitle}
      </Typo>
      <InfoRow label={labels.banner} value={banner.banner_group} />
      <InfoRow label={labels.size} value={`${banner.width}×${banner.height}`} />
      <InfoRow label={labels.language} value={languageLabel} />
      <InfoDivider />
      <InfoRow label={labels.type} value={types[banner.type]} />
      {showCampaign && (
        <InfoRow
          label={labels.campaign}
          value={campaignName ?? translations.columns.noCampaign}
          valueTextColor={campaignName ? undefined : "text-text-disabled"}
          wrapValue
        />
      )}
      <InfoRow
        label={labels.rebateProgram}
        value={formatRebateProgram(banner.hoid)}
      />
      <InfoDivider />
      <InfoRow label={labels.created} value={createdLabel} />
      {showLastClicked ? (
        <InfoRow
          label={labels.lastClicked}
          value={lastClickedLabel}
          valueTextColor={hasLastClick ? undefined : "text-text-disabled"}
        />
      ) : null}
    </div>
  );
};

type InfoRowProps = {
  label: string;
  value: ReactNode;
  valueTextColor?: string;
  wrapValue?: boolean;
};

const InfoRow = ({ label, value, valueTextColor, wrapValue }: InfoRowProps) => (
  <div className="flex w-full items-start justify-between gap-4">
    <Typo
      type="bodySM"
      textColor="text-text-secondary"
      className={clsx(wrapValue && "shrink-0")}
    >
      {label}
    </Typo>
    <Typo
      type="bodySM"
      textColor={valueTextColor ?? "text-text-primary"}
      className={clsx("break-words text-right", wrapValue && "min-w-0")}
    >
      {value}
    </Typo>
  </div>
);

const InfoDivider = () => (
  <div aria-hidden className="h-px w-full bg-stroke-primary" />
);
